import { Capacitor } from '@capacitor/core';

/**
 * Das dritte Gleis — die **eine** Stelle, an der die App entscheidet, worauf sie läuft.
 *
 * Bis zum 2026-09-22 gab es zwei Gleise, und jedes Modul entschied für sich mit
 * `Capacitor.isNativePlatform()`. Mit der Electron-Hülle (`desktop/`) sind es drei:
 *
 * ```
 * Capacitor.isNativePlatform()  → iPad, Swift
 * window.fairexa vorhanden      → Desktop
 * sonst                         → Browser
 * ```
 *
 * ⭐ **Erkannt wird die Brücke, nicht das Betriebssystem.** `navigator.userAgent` nach
 * „Electron" zu durchsuchen wäre eine Vermutung; `window.fairexa` ist eine Zusage: es steht
 * genau dann da, wenn `desktop/preload.cts` es gelegt hat — und damit genau dann, wenn die
 * Türen zum Rechner wirklich offen sind. Ein Desktop ohne Brücke wäre ein Browser, und
 * genau so soll er dann auch behandelt werden.
 *
 * ⚠️ **Nativ schlägt Desktop.** Die Reihenfolge ist keine Kosmetik: liefe die App je in einer
 * Hülle, die beides anbietet, gewönne das Gerät. Dort sind die Plugins echt, und alles, was
 * der Desktop nachbaut, ist der Ersatz.
 *
 * ⬅️ **Diese Datei benennt die drei Gleise, sie verzweigt nichts von sich aus.** Ausgetauscht
 * wird an Ort und Stelle: der Ordner (E3), SQLite (E4), Abzug, Schlüssel, Wachhalten und
 * Zeit (E5) — jedes in seinem Modul, jedes mit einer eigenen Etappe. S.
 * `docs/plan-desktop-und-browser.md`.
 */

/**
 * Was `desktop/preload.cts` ins Fenster legt — hier **noch einmal** beschrieben.
 *
 * ⚠️ **Zwei Beschreibungen derselben Sache, und das ist Absicht.** `src` und `desktop` sind
 * getrennte TypeScript-Projekte (`tsconfig.app.json` gegen `tsconfig.desktop.json`): jenes
 * kennt `electron` und CommonJS, dieses den Browser. Ein Import über die Grenze zöge die
 * Elektron-Typen in die App. Bleibt die Pflicht, beide Seiten zusammen zu ändern — deshalb
 * steht der Verweis in `preload.cts` ebenso wie hier.
 */
export interface DesktopBruecke {
  readonly gleis: 'desktop';
  /** `win32`, `darwin`, `linux` — für Meldungen und das Dateilog, nicht für Verzweigungen. */
  readonly plattform: string;
  /** Fassung der Hülle, nicht der App: die steht in `platform/appVersion.ts`. */
  readonly huelle: string;
  /** `process.getSystemVersion()`: macOS „15.5“, Windows „10.0.22631“ — für Relays `osVersion`. */
  readonly systemVersion: string;
  /**
   * Das echte Dateisystem unter `~/Documents/fairexa Stage`.
   *
   * ⚠️ **Nur `platform/dateien.ts` greift danach.** Wer hier vorbeigeht, umgeht die Fassade
   * und damit die Übersetzung der Fehler und die Einhegung der Pfade.
   */
  readonly dateien: DesktopDateien;
  /**
   * Echtes SQLite bei den Nutzerdaten.
   *
   * ⚠️ **Nur `persistence/desktop.ts` greift danach.** Es ist ein Rohr, kein Adapter: Schema
   * und SQL-Erzeugung bleiben in der App, damit es sie nur einmal gibt.
   */
  readonly ablage: DesktopAblage;
  /**
   * Ein Bild des Fensters, **ohne Freigabedialog** — s. `platform/screenshot.ts`.
   *
   * ⭐ Im Browser führt jeder Abzug über `getDisplayMedia`, und wer dort „ganzer Bildschirm"
   * statt „dieser Tab" wählt, hat die Adressleiste auf dem Bogenabzug. Hier fragt niemand.
   */
  abzug(angaben: { qualitaet: number; skalierung: number }): Promise<{
    base64: string;
    width: number;
    height: number;
    format: 'jpg' | 'png';
  }>;
  /**
   * Den Bildschirm wach halten, solange eine Station läuft — `powerSaveBlocker`.
   *
   * ⚠️ Am iPad tut das ein eigenes Swift-Plugin (`platform/wach.ts`); im Browser gibt es
   * nichts Vergleichbares, das auch auf iOS 15 trüge.
   */
  wach(an: boolean): Promise<{ an: boolean }>;
  /** Der Name des Rechners (`os.hostname()`) — Vorgabe des Gerätenamens (`geraetename.ts`). */
  rechnername(): Promise<string>;
  /**
   * Der Signaturschlüssel im Hauptprozess.
   *
   * ⛔ **Keine Tür gibt den privaten Teil heraus** — sonst läge er beim ersten Aufruf im
   * Fenster und damit in jedem Speicherabbild davon.
   */
  readonly schluessel: {
    verfuegbar(): Promise<boolean>;
    identitaet(): Promise<{ publicKey: string; keyId: string }>;
    signiere(nutzlast: string): Promise<string>;
  };
  /**
   * Die Rechneruhr gegen einen Zeitserver halten — SNTP über UDP 123 (`desktop/zeit.cts`).
   *
   * ⚠️ **Wirft nicht.** Kein Netz, gesperrter Anschluss, unbekannter Name: alles kommt als
   * `{ available: false }` zurück, und dann bleibt es bei `timeSource: 'system'` — ohne
   * Meldung an den Prüfer (s. `platform/clockCheck.ts`).
   */
  zeit(server: string, fristMs?: number): Promise<{ available: boolean; offsetSeconds?: number }>;
  /** Darf der Hauptprozess Fehlerberichte senden? (`desktop/fehlerberichte.cts`) */
  fehlerberichte(an: boolean, release: string): Promise<void>;
  /**
   * Schluss machen — nach bestandener Abfrage, s. `features/beenden/beendenSperre.ts`.
   *
   * ⚠️ **Die Hülle prüft nichts.** Sie beendet, wenn diese Tür gerufen wird; das Schloss steht
   * im Fenster. Beides drüben zu führen hiesse, das Wort ein zweites Mal abzulegen.
   */
  beenden(): Promise<void>;
  /**
   * Der Wunsch der Hülle, beendet zu werden — Menü, ⌘Q/Alt+F4, Schliessknopf.
   *
   * Sie hält an all diesen Stellen an und fragt hier nach. Gibt die Abbestellung zurück.
   */
  aufBeendenwunsch(hoerer: () => void): () => void;
  /**
   * Beschriftet das Menü der Hülle.
   *
   * ⚠️ **Die Sprache steht hier, nicht drüben.** Der Hauptprozess kennt den Katalog nicht; er
   * bekommt die vier Wörter aus `i18n/`, damit sie nicht an zwei Orten liegen.
   */
  menue(texte: {
    beenden: string;
    datei: string;
    bearbeiten: string;
    ansicht: string;
  }): Promise<void>;
  /** Die Argumente des ersten Aufrufs, einmal — für `--preview=` (`preview/previewEntry.ts`). */
  aufrufArgumente(): Promise<string[]>;
  /** Die Argumente jedes Zweitaufrufs. Gibt die Abbestellung zurück. */
  aufZweitaufruf(hoerer: (argv: string[]) => void): () => void;
}

/** Das Rohr zur Ablage — Gegenstück zu `AblageBruecke` in `desktop/preload.cts`. */
export interface DesktopAblage {
  oeffne(
    aufstiege: readonly { bis: number; saetze: readonly string[] }[],
  ): Promise<{ fassung: number; gestiegenVon: number }>;
  ausfuehren(sql: string, werte: readonly (string | number | null)[]): Promise<void>;
  abfragen(sql: string, werte: readonly (string | number | null)[]): Promise<unknown[]>;
  beginne(): Promise<void>;
  schreibeFest(): Promise<void>;
  nimmZurueck(): Promise<void>;
  schliesse(): Promise<void>;
  pfad(): Promise<string>;
}

/** Die Türen zum Dateisystem — Gegenstück zu `DateiBruecke` in `desktop/preload.cts`. */
export interface DesktopDateien {
  liesText(pfad: string): Promise<string>;
  liesBase64(pfad: string): Promise<string>;
  liesGepackt(pfad: string): Promise<{ base64: string; crc: number; groesse: number }>;
  schreibeText(pfad: string, text: string, ordnerAnlegen: boolean): Promise<void>;
  schreibeBase64(pfad: string, base64: string, ordnerAnlegen: boolean): Promise<void>;
  haengeAn(pfad: string, text: string): Promise<void>;
  listeOrdner(pfad: string): Promise<{ dateien: string[]; ordner: string[] }>;
  legeOrdnerAn(pfad: string): Promise<void>;
  gibtEs(pfad: string): Promise<boolean>;
  benenneUm(von: string, nach: string): Promise<void>;
  loescheDatei(pfad: string): Promise<void>;
  loescheOrdner(pfad: string): Promise<void>;
  adresse(pfad: string): string;
  /** Der absolute Pfad der Wurzel — für die Prüfung der Pfadlänge (`platform/pfadlaenge.ts`). */
  wurzel(): Promise<string>;
}

/**
 * Die Brücke zum Rechner — oder `null`, wenn es keine gibt.
 *
 * Der **einzige** Zugriff auf `window.fairexa` in der ganzen App. Wer etwas vom Rechner
 * braucht, holt es hier und findet `null` vor, wenn er im Browser oder auf dem iPad läuft.
 */
export function desktopBruecke(): DesktopBruecke | null {
  const bruecke = (globalThis as { fairexa?: DesktopBruecke }).fairexa;
  return bruecke?.gleis === 'desktop' ? bruecke : null;
}

/** Das iPad: echte Plugins, echtes Dateisystem, echtes SQLite, Secure Enclave. */
export function istGeraet(): boolean {
  return Capacitor.isNativePlatform();
}

/** Die Electron-Hülle auf Windows oder macOS. */
export function istDesktop(): boolean {
  return !istGeraet() && desktopBruecke() !== null;
}

/** Ein gewöhnlicher Browser — weder Gerät noch Hülle. */
export function istBrowser(): boolean {
  return !istGeraet() && !istDesktop();
}
